import * as React from 'react';
import * as qs from 'qs';

import { Check, CheckDialog } from 'common/components/dialogs/CheckDialog';
import { Col, Row } from 'emotion-jsxstyle';
import { EntityActions, EntityType } from 'common/entity';
import { FormGroup, Label } from 'reactstrap';
import { History, Location } from 'history';
import StripeCheckout, { Token } from 'react-stripe-checkout';
import { AlertDialog } from 'common/components/dialogs/AlertDialog';
import { Account } from 'common/api/models/Account';
import { Address } from 'common/api/models/Address';
import { AddressBlock } from 'common/components/AddressBlock';
import { AppState } from 'common/models/AppState';
import { ConfirmDialog } from 'common/components/dialogs/ConfirmDialog';
import { EditAddressDialog } from 'common/components/dialogs/EditAddressDialog';
import { FormAutosuggestProduct } from 'common/components/form/FormAutosuggestProduct';
import { Loading } from 'common/components/Loading';
import { Money } from 'common/components/Money';
import { OrderApi } from 'common/api/resources/OrderApi';
import { OrderItem } from 'common/api/models/OrderItem';
import { OrderRequest } from '../../common/api/models/OrderRequest';
import { Page } from 'common/components/layout/Page/Page';
import { Product } from 'common/api/models/Product';
import { RowActionButton } from 'common/components/RowActionButton';
import { MemberLink } from 'common/components/link/MemberLink';
import { toApiError } from 'common/utils/api';
import { Member } from 'common/api/models/Member';
import { FormAutosuggestMember } from 'common/components/form/FormAutosuggestMember';
import { MemberApi } from 'common/api/resources/MemberApi';

export interface CheckoutProps {
  appState: AppState;
  entityActions: EntityActions;
  history: History;
  location: Location;
}
export interface CheckoutState {
  isMemberLoading: boolean;
  isOrderLoading: boolean;
  isProductLoading: boolean;

  member?: Member;

  createAccount?: Account;
  createAccountEntity?: { id: string; type: EntityType };
  items: OrderItem[];
  billingAddress?: Address;
  shippingAddress?: Address;
  checkNumber?: string;
  error?: string;
  dialogError?: string;
  editAddress?: string;

  autorenew: boolean;

  showPendingDialog: boolean;
  showCheckDialog: boolean;
  alert?: string;
}

export class Checkout extends React.Component<CheckoutProps, CheckoutState> {
  state: CheckoutState = {
    isMemberLoading: true,
    isOrderLoading: true,
    isProductLoading: true,
    items: [],
    showCheckDialog: false,
    showPendingDialog: false,
    autorenew: false,
  };

  componentDidMount() {
    this.props.entityActions.fetchAll(EntityType.PRODUCT, () => {
      const params = qs.parse(this.props.location.search.substring(1));
      this.setState({ isProductLoading: false });
      if (params.memberId) {
        this.props.entityActions.fetch<Member>(EntityType.MEMBER, params.memberId).then(
          member => {
            let billingAddress = member.billingAddress ? member.billingAddress : member.workAddress;
            let shippingAddress = member.shippingAddress ? member.shippingAddress : member.workAddress;
            this.setState({ member, billingAddress, shippingAddress, isMemberLoading: false });
          },
          err => {
            this.setState({ error: toApiError(err) });
          }
        );
      } else {
        this.setState({ isMemberLoading: false });
      }

      if (params.memberId && params.renew) {
        this.loadRenewOrder(params.memberId);
      } else if (params.memberId && params.addBranchCount) {
        this.loadLocationOrder(params.memberId, params.addBranchCount);
      } else if (params.cert && params.memberId) {
        this.loadSku(params.memberId, 'CERT-' + params.cert, true);
      } else if (params.sku && params.memberId) {
        this.loadSku(params.memberId, params.sku, true);
      } else {
        this.setState({ isOrderLoading: false });
      }
    });
  }

  loadRenewOrder(memberId: string) {
    OrderApi.getDuesRenewalOrder(memberId).then(
      res => {
        let fetches = res.items!.map(i => this.props.entityActions.fetch(EntityType.PRODUCT, i.productId!));
        Promise.all(fetches).then(r2 => {
          this.setState({
            items: res.items!,
            isOrderLoading: false,
          });
        });
      },
      err => {
        this.setState({ error: toApiError(err), isOrderLoading: false });
      }
    );
  }

  loadLocationOrder(memberId: string, branchCount: number) {
    OrderApi.getAdditionalLocationsOrder(memberId, { qty: branchCount }).then(
      res => {
        let fetches = res.items!.map(i => this.props.entityActions.fetch(EntityType.PRODUCT, i.productId!));
        Promise.all(fetches).then(r2 => {
          this.setState({
            items: res.items!,
            isOrderLoading: false,
          });
        });
      },
      err => {
        this.setState({ error: toApiError(err), isOrderLoading: false });
      }
    );
  }

  dedupeOrder(items: OrderItem[]) {
    let itemSkuMap: { [sku: string]: OrderItem } = {};
    for (let item of items) {
      if (!itemSkuMap[item.productId]) {
        itemSkuMap[item.productId] = item;
      } else {
        itemSkuMap[item.productId].qty += item.qty;
      }
    }
    let result: OrderItem[] = [];
    for (let k in itemSkuMap) {
      if (itemSkuMap.hasOwnProperty(k)) {
        result.push(itemSkuMap[k]);
      }
    }
    return result;
  }

  loadSku(memberId: string, sku: string, replaceOrder: boolean) {
    // load the prorated order, so we get the correct price
    OrderApi.getNewOrder(memberId, { sku }).then(
      proratedOrder => {
        let fetches = proratedOrder.items!.map(i => this.props.entityActions.fetch(EntityType.PRODUCT, i.productId!));
        Promise.all(fetches).then(r2 => {
          let items: OrderItem[] = [];
          if (replaceOrder) {
            items = proratedOrder.items!;
          } else {
            items = this.dedupeOrder([...this.state.items, ...proratedOrder.items!]);
          }
          this.setState({
            items,
            isOrderLoading: false,
          });
        });
      },
      err => {
        this.setState({ error: toApiError(err), isOrderLoading: false });
      }
    );
  }

  getOrderRequest(): OrderRequest {
    return {
      accountId: this.state.member!.id,
      entityId: this.state.member!.id,
      entityType:
        this.state.member!.type === Member.Type.ORG ? OrderRequest.EntityType.ORG : OrderRequest.EntityType.USER,
      items: this.state.items,
      billingAddress: this.state.billingAddress,
      shippingAddress: this.state.shippingAddress,
    };
  }

  createAccount(member: Member, type: EntityType) {
    /*
    const name = member.name ? member.name : member.firstName + ' ' + member.lastName;
    const address = member.workAddress ? member.workAddress : member.homeAddress;
    let createAccount: Account = {
      name: name,
      email: member.email,
      phone: member.homePhone ? member.homePhone : member.workAddress,
      billingAddress: address,
      shippingAddress: address,
      number: member.number,
    };
    this.setState({ createAccount: createAccount, createAccountEntity: { id: member.id, type: type } });
    */
  }

  handleSelectMember = (field: string, id: string) => {
    this.props.entityActions.fetch<Member>(EntityType.MEMBER, id).then(res => {
      let billingAddress = res.billingAddress ? res.billingAddress : res.workAddress;
      let shippingAddress = res.shippingAddress ? res.shippingAddress : res.workAddress;
      this.setState({ member: res, billingAddress, shippingAddress });
    });
  };

  handleSaveCreateAccount = (id: string) => {
    return;
  };

  handleCancelCreateAccount = () => {
    this.setState({ createAccount: undefined });
  };

  handleAddProduct = (field: string, productId: string) => {
    // DEV-51 Disallow adding of certain product types if member renewal date is passed
    this.props.entityActions.fetch<Product>(EntityType.PRODUCT, productId).then(prod => {
      const todayDate = new Date();
      let isActiveMembership = false;
      let isProratedItem = false;
      let memberExpireDate;

      // Check to see if product is prorated
      if (
        prod.type! === Product.Type.LOCATIONS // |0|
        // prod.type! === Product.Type.CERT_NEW ||
        // prod.type! === Product.Type.WIKI
      ) {
        isProratedItem = true;
      }

      // Check if an active member
      if (prod.type === Product.Type.WIKI || prod.type === Product.Type.DUES) {
        // Wiki and Dues doesn't require any membership so we'll set ActiveMembership to true
        isActiveMembership = true;
      } else if (this.state.member!.expireDate) {
        memberExpireDate = new Date(this.state.member!.expireDate!);
        if (memberExpireDate >= todayDate) {
          isActiveMembership = true;
        }
      }

      if (!isActiveMembership && isProratedItem) {
        this.setState({ alert: `You must join or renew your membership to add ${prod.name}.` });
      } else {
        this.loadSku(this.state.member!.id!, prod.sku!, false);
      }
    });
  };

  handleChangeQty = (e: React.ChangeEvent<HTMLInputElement>) => {
    let qty = e.target.value;
    if (Number(qty) >= 0 && Number(qty) < 10000) {
      let row = e.target.dataset.row;
      let items = this.state.items;
      items[row!].qty = qty;
      this.setState({ items: items });
    }
  };

  handleDeleteRow = (row: number) => {
    let items = this.state.items;
    items.splice(row, 1);
    this.setState({ items: items });
  };

  handleCheckout = (e: React.MouseEvent<any>) => {
    e.preventDefault();
  };

  handleMarkPaid = () => {
    this.setState({ showCheckDialog: true });
  };

  handleMarkPending = () => {
    this.setState({ showPendingDialog: true });
  };

  handleCancelPending = () => {
    this.setState({ showPendingDialog: false });
  };

  handleCancelCheck = () => {
    this.setState({ showCheckDialog: false });
  };

  handleChangeAutorenew = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ autorenew: !this.state.autorenew });
  };

  handleChangeAddress = (e: React.MouseEvent<HTMLButtonElement>) => {
    const field = e.currentTarget.dataset.field;
    this.setState({ editAddress: field });
  };

  handleSaveAddress = (address: Address) => {
    if (this.state.editAddress === 'billingAddress') {
      this.setState({ editAddress: undefined, billingAddress: address });
    } else {
      this.setState({ editAddress: undefined, shippingAddress: address });
    }
  };

  handleCancelAddress = () => {
    this.setState({ editAddress: undefined });
  };

  handleStripeToken = (token: Token) => {
    let request = this.getOrderRequest();
    request.token = token.id;
    if (this.state.autorenew) {
      request.updateStripeCustomer = true;
    }
    this.setState({ isOrderLoading: true, error: undefined });
    OrderApi.createOrder(request).then(
      res => {
        const update: Promise<any> = this.state.autorenew
          ? MemberApi.updateMember(request.accountId!, { renewType: Member.RenewType.AUTO })
          : Promise.resolve();
        update.then(
          result => {
            this.setState({ isOrderLoading: false, error: undefined });
            this.props.history.push('/orders/' + res.number);
          },
          err => {
            this.setState({ isOrderLoading: false, error: toApiError(err) });
          }
        );
      },
      err => {
        this.setState({ isOrderLoading: false, error: toApiError(err) });
      }
    );
  };

  handleSaveCheck = (check: Check) => {
    let request = this.getOrderRequest();
    request.checkNumber = String(check.checkNumber);
    request.amount = check.amount;
    this.setState({ isOrderLoading: true, error: undefined });
    OrderApi.createOrder(request).then(
      res => {
        this.setState({ isOrderLoading: false, error: undefined });
        this.props.history.push('/orders/' + res.number);
      },
      err => {
        this.setState({ isOrderLoading: false, dialogError: toApiError(err) });
      }
    );
  };

  handleConfirmPending = () => {
    let request = this.getOrderRequest();
    OrderApi.createOrder(request).then(
      res => {
        this.props.history.push('/orders/' + res.number);
      },
      err => {
        this.setState({ dialogError: toApiError(err) });
      }
    );
  };

  handleCloseAlert = () => {
    this.setState({ alert: undefined });
  };

  render() {
    const { isMemberLoading, isProductLoading, isOrderLoading } = this.state;
    if (isMemberLoading || isProductLoading || isOrderLoading) {
      return (
        <Page title="Create Order" breadcrumbs={[{ label: 'Orders', to: '/orders' }]}>
          <Loading />
        </Page>
      );
    }

    const products = this.props.appState.entities.product;
    let total = 0;
    for (let item of this.state.items) {
      total += item.qty * item.price;
    }

    const editAddress: Address = this.state.editAddress ? this.state[this.state.editAddress] : undefined;
    const member = this.state.member;

    return (
      <Page title="Create Order" breadcrumbs={[{ label: 'Orders', to: '/orders' }]} testId="checkout">
        {!member && (
          <FormGroup>
            <Label>Select Member</Label>
            <FormAutosuggestMember
              id="autosuggest"
              autoFocus={true}
              appState={this.props.appState}
              onChange={this.handleSelectMember}
            />
          </FormGroup>
        )}
        {member && (
          <div>
            <Row>
              <Col>
                <div>
                  {member && (
                    <b>
                      Member: <MemberLink member={member} /> ({member.number})
                    </b>
                  )}
                </div>
              </Col>
            </Row>
            <div className="row" style={{ marginTop: 20 }}>
              <div className="col col-3">
                <div>
                  <b>Billing Address</b>{' '}
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={this.handleChangeAddress}
                    data-field="billingAddress"
                  >
                    Change
                  </button>{' '}
                </div>
                <AddressBlock address={this.state.billingAddress} />
              </div>
              <div className="col col-3">
                <div>
                  <b>Shipping Address</b>{' '}
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={this.handleChangeAddress}
                    data-field="shippingAddress"
                  >
                    Change
                  </button>
                </div>
                <AddressBlock address={this.state.shippingAddress} />
              </div>
            </div>
            <FormGroup style={{ marginTop: '2rem' }}>
              <Label style={{ fontWeight: 'bold', fontSize: '1.25rem' }}>Add Product</Label>
              <FormAutosuggestProduct
                id="addProduct"
                appState={this.props.appState}
                onChange={this.handleAddProduct}
                autoFocus={true}
              />
            </FormGroup>
          </div>
        )}
        {member &&
          this.state.items.length > 0 && (
            <div>
              <div className="row" style={{ marginTop: 10 }}>
                <div className="col">
                  <table className="table table-striped table-hover" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product</th>
                        <th>Price</th>
                        <th style={{ width: 20 }}>Qty</th>
                        <th style={{ width: 120 }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.state.items.map((item, i) => (
                        <tr key={i}>
                          <td>{products[item.productId!] && products[item.productId!].sku}</td>
                          <td>{item.name}</td>
                          <td>
                            <Money value={item.price} />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={item.qty}
                              data-row={i}
                              onChange={this.handleChangeQty}
                              style={{ width: 80 }}
                            />
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <Money value={item.price! * item.qty!} />
                          </td>
                          <td>
                            <RowActionButton icon="trash" item={i} onClick={this.handleDeleteRow} />
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={4} />
                        <td style={{ textAlign: 'right' }}>
                          <b>
                            <Money value={total} />
                          </b>
                        </td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="row">
                <div className="col" style={{ textAlign: 'right' }}>
                  <label>
                    <input type="checkbox" checked={this.state.autorenew} onChange={this.handleChangeAutorenew} />{' '}
                    Auto-Renew
                  </label>
                </div>
              </div>
              <div className="row">
                <div className="col" style={{ textAlign: 'right' }}>
                  <button className="btn btn-secondary" onClick={this.handleMarkPaid}>
                    <i className="fa fa-check-square-o" /> Mark as Paid
                  </button>{' '}
                  <button className="btn btn-secondary" onClick={this.handleMarkPending}>
                    <i className="fa fa-square-o" /> Mark as Pending
                  </button>{' '}
                  <StripeCheckout
                    name="Charge Credit Card"
                    email="cloud@nkba.org"
                    currency="USD"
                    amount={total * 100}
                    token={this.handleStripeToken}
                    stripeKey={process.env.REACT_APP_STRIPE_PUBLIC_KEY!}
                    allowRememberMe={false}
                  >
                    <button className="btn btn-primary" onClick={this.handleCheckout}>
                      <i className="fa fa-credit-card" /> Charge Credit Card
                    </button>
                  </StripeCheckout>
                </div>
              </div>
            </div>
          )}
        {this.state.error && (
          <div className="alert alert-danger" style={{ marginTop: 20 }}>
            {this.state.error}
          </div>
        )}
        <EditAddressDialog address={editAddress} onSave={this.handleSaveAddress} onCancel={this.handleCancelAddress} />
        <CheckDialog
          title="Process Check Payment"
          saveLabel="Create Order"
          isOpen={this.state.showCheckDialog}
          onSave={this.handleSaveCheck}
          onCancel={this.handleCancelCheck}
          error={this.state.dialogError}
        />
        <ConfirmDialog
          title="Mark as Payment Pending"
          message={
            <div>
              <p>Please confirm the order total is correct and that the check will be processed later.</p>
              <p>
                <b>
                  Total: <Money value={total} />
                </b>
              </p>
            </div>
          }
          confirmLabel="Create Order"
          isOpen={this.state.showPendingDialog}
          onConfirm={this.handleConfirmPending}
          onCancel={this.handleCancelPending}
          error={this.state.dialogError}
        />
        <AlertDialog
          isOpen={!!this.state.alert}
          title="Error"
          message={this.state.alert ? this.state.alert : ''}
          onClose={this.handleCloseAlert}
        />
      </Page>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Checkout/Checkout.tsx